import type { ReactNode } from 'react'

export type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info'

export interface StatusBadgeProps {
  tone?: Tone
  /** The label. Required — a badge is never colour alone. */
  children: ReactNode
  showDot?: boolean
}

/**
 * StatusBadge — a compact state label.
 *
 * Text-first by construction: `children` is required, so there is no way to
 * render a badge that is only a coloured dot. A colour-only badge is a legend
 * the user has to memorise and is invisible to anyone who cannot distinguish
 * the hues (WCAG 1.4.1).
 */
export function StatusBadge({ tone = 'neutral', children, showDot = true }: StatusBadgeProps) {
  return (
    <span className={`lg-badge lg-badge--${tone}`}>
      {showDot && <span className="lg-badge__dot" aria-hidden="true" />}
      {children}
    </span>
  )
}

/** Maps HR employment status to a tone and a human label. */
export const employeeStatusTone: Record<string, { tone: Tone; label: string }> = {
  active: { tone: 'success', label: 'Active' },
  onboarding: { tone: 'info', label: 'Onboarding' },
  on_leave: { tone: 'warning', label: 'On leave' },
  suspended: { tone: 'warning', label: 'Suspended' },
  terminated: { tone: 'neutral', label: 'Terminated' },
}
